import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useToken } from './TokenContext';

const SSEContext = createContext(null);

export const SSEProvider = ({ children }) => {
  const { token } = useToken();
  const eventSourceRef = useRef(null);
  const listenersRef = useRef(new Map());
  const forwardersRef = useRef(new Map());
  const attachedEventsRef = useRef(new Set());

  const removeForwarderIfUnused = useCallback((eventName) => {
    const listeners = listenersRef.current.get(eventName);
    if (listeners?.size) {
      return;
    }

    const eventSource = eventSourceRef.current;
    const forwarder = forwardersRef.current.get(eventName);

    if (eventSource && forwarder && attachedEventsRef.current.has(eventName)) {
      eventSource.removeEventListener(eventName, forwarder);
    }

    attachedEventsRef.current.delete(eventName);
    forwardersRef.current.delete(eventName);
    listenersRef.current.delete(eventName);
  }, []);

  const ensureForwarderAttached = useCallback((eventName, eventSource = eventSourceRef.current) => {
    if (!eventSource || attachedEventsRef.current.has(eventName)) {
      return;
    }

    let forwarder = forwardersRef.current.get(eventName);

    if (!forwarder) {
      forwarder = (event) => {
        const handlers = listenersRef.current.get(eventName);
        if (!handlers?.size) {
          return;
        }

        handlers.forEach((handler) => {
          try {
            handler(event);
          } catch (error) {
            console.error(`Erro ao processar evento SSE "${eventName}":`, error);
          }
        });
      };

      forwardersRef.current.set(eventName, forwarder);
    }

    eventSource.addEventListener(eventName, forwarder);
    attachedEventsRef.current.add(eventName);
  }, []);

  const subscribeToEvent = useCallback((eventName, handler) => {
    if (!eventName || typeof handler !== 'function') {
      return () => {};
    }

    const listeners = listenersRef.current.get(eventName) || new Set();
    listeners.add(handler);
    listenersRef.current.set(eventName, listeners);

    ensureForwarderAttached(eventName);

    return () => {
      const currentListeners = listenersRef.current.get(eventName);
      if (!currentListeners) {
        return;
      }

      currentListeners.delete(handler);

      if (!currentListeners.size) {
        removeForwarderIfUnused(eventName);
      }
    };
  }, [ensureForwarderAttached, removeForwarderIfUnused]);

  useEffect(() => {
    if (!token || token === 'null') {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      attachedEventsRef.current = new Set();
      return undefined;
    }

    const baseURL = window.APP_CONFIG?.Url_Api || 'http://default-url.com';
    const url = `${baseURL}/stream?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(url);

    eventSourceRef.current = eventSource;
    attachedEventsRef.current = new Set();

    listenersRef.current.forEach((_handlers, eventName) => {
      ensureForwarderAttached(eventName, eventSource);
    });

    eventSource.onerror = (error) => {
      console.error('Erro na conexao SSE global:', error);
    };

    return () => {
      eventSource.close();

      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }

      attachedEventsRef.current = new Set();
    };
  }, [token, ensureForwarderAttached]);

  return (
    <SSEContext.Provider value={{ subscribeToEvent }}>
      {children}
    </SSEContext.Provider>
  );
};

export const useSSE = () => {
  const context = useContext(SSEContext);

  if (!context) {
    throw new Error('useSSE must be used within a SSEProvider');
  }

  return context;
};
